<template>
  <v-dialog
    v-model="showDialog"
    width="900"
    content-class="primary-confirm-dialog payment-dialog elevation-12 position-relative"
    persistent
    :overlay-opacity="0.6"
    transition="primary-confirm-transition"
  >
    <loader v-if="!currentEditOrder || isLoading" absolute />
    <div class="payment-dialog-content">
      <v-row>
        <v-col sm="5" class="left-side-menu">
          <div class="d-flex">
            <large-btn
              class="cancel-button primary-confirm-dialog_btn"
              color="#fff"
              height="64px"
              :uppercase="false"
              :disabled="(paymentProcessing && !isTerminalWaiting) || isTerminalPaid"
              @click="onCancelClick"
            >
              <font-awesome-icon icon="times" class="mr-3" color="red" />
              Отмена
            </large-btn>
            <manual-mode-button
              v-if="showManualButton"
              class="manual-button ml-2"
              color="#fff"
              auto-label="Авто режим"
              manual-icon="mdi-dialpad"
              auto-hint="Вернуться в авто режим по терминалу"
              :manual="manualMode"
              :disabled="terminalState === 'success'"
              @toggle="manualMode = !manualMode"
            />
          </div>
          <user-info class="mt-12 mb-8" :user="user" />
          <div v-if="currentEditOrder" class="order-code mb-12">
            Заказ №{{ currentEditOrder.code }}
          </div>
          <div class="transaction-currency-section">
            <payment-type-select
              v-model="selectedType"
              :types="paymentTypes"
              :enabled-types="acceptedTypes"
              :disabled="paymentProcessing || paymentTypeLocked"
            />
          </div>
        </v-col>
        <v-col sm="7">
          <!-- вью живут только пока окно открыто: закрытие размонтирует их и гасит поллинг, сброса руками нет -->
          <v-tabs-items v-if="showDialog && !isLoading" v-model="tab">
            <v-tab-item value="none">
              <payment-not-available-view :total="totalPrice" @close="closeDialog" />
            </v-tab-item>
            <v-tab-item value="cash">
              <cash-payment-view
                :total="totalPrice"
                :client-money.sync="clientMoney"
                :loading.sync="paymentProcessing"
                :order-id="orderId"
                @paid="exit"
              />
            </v-tab-item>
            <v-tab-item value="terminal">
              <terminal-payment-view
                :total="totalPrice"
                :order-id="orderId"
                :order-code="currentEditOrder && currentEditOrder.code"
                :loading.sync="paymentProcessing"
                :manual.sync="manualMode"
                :payment-settings="terminalPaymentSettings"
                :active-operation="activeOperation"
                @paid="exit"
                @state="terminalState = $event"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
      <!-- терминал ждёт карту: закрытие окна оплату не отменяет, а KkmServer прервать её не умеет -->
      <primary-confirm-dialog
        v-model="showWaitingCloseConfirm"
        :persistent="false"
        title="Закрыть окно оплаты?"
        subtitle="CRM не может прервать оплату на терминале. Нажмите красную кнопку на терминале. Если клиент приложит карту, оплата запишется сама."
        confirm-text="Закрыть окно"
        cancel-text="Ждать"
        @confirm="confirmCloseWhileWaiting"
      />
    </div>
  </v-dialog>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import snackbarMessage from '@/mixins/snackbar-message'
  import CashPaymentView from './payment-dialog/CashPaymentView.vue'
  import TerminalPaymentView from './payment-dialog/TerminalPaymentView.vue'
  import PaymentNotAvailableView from './payment-dialog/PaymentNotAvailableView.vue'
  import PaymentType from '@/models/enums/PaymentType'
  import PaymentStatus from '@/models/enums/PaymentStatus'
  import CRMPermission from '@/models/enums/permissions/CRMPermission'
  import PaymentTypeSelect from './payment-dialog/PaymentTypeSelect.vue'
  import UserInfo from './payment-dialog/UserInfo.vue'
  import ManualModeButton from './payment-dialog/ManualModeButton.vue'
  import paymentApi from '@/api/order/payment'
  import Loader from '@/components/loader/Loader.vue'
  import LargeBtn from '@/components/ui/buttons/LargeBtn.vue'
  import PrimaryConfirmDialog from '@/components/ui/dialogs/PrimaryConfirmDialog.vue'

  export default {
    name: 'PaymentDialog',
    components: {
      CashPaymentView,
      TerminalPaymentView,
      PaymentNotAvailableView,
      PaymentTypeSelect,
      UserInfo,
      ManualModeButton,
      LargeBtn,
      Loader,
      PrimaryConfirmDialog,
    },
    mixins: [snackbarMessage],
    props: {
      order: Object,
    },
    data () {
      return {
        isLoading: false,
        paymentProcessing: false,
        selectedType: PaymentType.Cash,
        tab: 'cash',
        clientMoney: 0,
        paymentTypes: [],
        acceptedTypes: null,
        terminalState: null,
        manualMode: false,
        // оплата терминалом, начатая в прошлый раз и ещё не завершённая: окно продолжит её ждать
        activeOperation: null,
        showWaitingCloseConfirm: false,
      }
    },
    computed: {
      ...mapState({
        showDialog: state => state.orders.showPaymentDialog,
        currentEditOrder: state => state.orders.currentEditOrder,
        basket: state => state.baskets.basket,
      }),
      ...mapGetters({
        user: 'auth/getUser',
        filialId: 'branches/getGlobalFilialId',
      }),
      orderId () {
        return this.currentEditOrder?.id
      },
      terminalPaymentSettings () {
        return this.paymentTypes.find(item => item.type === PaymentType.Terminal)?.settings
      },
      totalPrice () {
        return this.basket?.totalPrice || this.currentEditOrder?.basket?.totalPrice
      },
      initialClientMoney () {
        return this.basket?.clientMoney || this.currentEditOrder?.basket?.clientMoney || 0
      },
      availablePaymentTypes () {
        return this.acceptedTypes ? this.paymentTypes.filter(item => this.acceptedTypes.includes(item.type)) : this.paymentTypes
      },
      isTerminalWaiting () {
        return this.terminalState === 'loading'
      },
      // «Оплата завершена!»: окно закрывает только «Готово» - оно перечитывает заказ на странице заказа
      isTerminalPaid () {
        return this.terminalState === 'success'
      },
      // терминал списал деньги или мог списать: оплата другим способом - второй платёж за заказ.
      // Незаписанную оплату бэк и сам не пустит (UNAPPLIED_PAY_MESSAGE), здесь - до клика. Из «оплата не закрыта»
      // наличные доступны через «Закрыть оплату вручную»: оператор сначала сверяется с терминалом
      paymentTypeLocked () {
        return ['success', 'applying', 'confirmedUnknown'].includes(this.terminalState)
      },
      // пока учёт пишет списанную оплату, ручная оплата - второй платёж за те же деньги
      showManualButton () {
        return this.selectedType === PaymentType.Terminal &&
          Boolean(this.terminalPaymentSettings?.available) &&
          this.terminalState !== 'applying'
      },
    },
    watch: {
      showDialog () {
        if (this.showDialog) this.handleOpen()
      },
      selectedType () {
        this.tab = this.selectedType || 'none'
      },
      // ожидание закончилось: вернувшись на вкладку терминала, старую операцию заново не подхватываем
      terminalState (state) {
        if (!['loading', 'applying'].includes(state)) this.activeOperation = null
        // терминал ответил, пока висел вопрос: «Закрыть окно» на «Оплата завершена!» не обновило бы заказ
        if (state !== 'loading') this.showWaitingCloseConfirm = false
      },
    },
    created () {
      if (this.showDialog) this.handleOpen()
    },
    methods: {
      handleOpen () {
        if (!this.userCan(CRMPermission.MakeOrderPayment)) {
          // this.showErrorMessage('Недостаточно прав для оплаты заказа')
          this.$store.commit('orders/setShowPaymentDialog', false)
          return
        }
        // последний рубеж: без заказа или по оплаченному заказу диалог не открывается,
        // откуда бы его ни дёрнули; сбрасываем и залипший режим-триггер
        if (!this.orderId || this.currentEditOrder?.payment?.status === PaymentStatus.Success) {
          this.$store.commit('newOrder/setPaymentMode', null)
          this.$store.commit('orders/setShowPaymentDialog', false)
          return
        }
        this.clientMoney = this.initialClientMoney
        this.manualMode = false
        this.terminalState = null
        this.activeOperation = null
        this.paymentProcessing = false
        this.showWaitingCloseConfirm = false
        this.initPayment()
      },
      initPayment () {
        this.isLoading = true

        Promise.allSettled([
          this.fetchAvailablePaymentTypes(),
          this.startPayment(),
          this.fetchActiveOperation(),
        ]).then(() => {
          if (this.activeOperation && this.terminalPaymentSettings?.available) {
            this.selectedType = PaymentType.Terminal
          }
          this.isLoading = false
        })
      },
      async fetchActiveOperation () {
        try {
          const operation = await paymentApi.fetchActiveTerminalOperation(this.orderId)
          this.activeOperation = operation?.type === 'pay' ? operation : null
        } catch (e) {
          // без неё окно просто начнёт с выбора оплаты, повторную оплату бэк не пустит
          this.activeOperation = null
        }
      },
      async startPayment () {
        try {
          await paymentApi.start(this.orderId)
        } catch (e) {
          this.showErrorFromResponse(e)
        }
      },
      // платят на точке, поэтому доступны методы самовывоза филиала заказа; null = набор не настроен
      async fetchAvailablePaymentTypes () {
        try {
          this.paymentTypes = await paymentApi.fetchTypes(this.currentEditOrder?.filialId || this.filialId)
          const accepted = this.currentEditOrder?.basket?.restaurantPaymentTypes
          this.acceptedTypes = Array.isArray(accepted) ? accepted : null
          this.initPaymentType()
        } catch (e) {
          this.showErrorFromResponse(e)
        }
      },
      initPaymentType () {
        let paymentType = this.availablePaymentTypes.find(item => item.type === this.currentEditOrder.paymentType)
        if (!paymentType && this.availablePaymentTypes.length) {
          paymentType = this.availablePaymentTypes[0]
        }

        this.selectedType = paymentType?.type
        this.tab = this.selectedType || 'none'
      },
      exit () {
        this.close()
        this.$emit('paid')
      },
      onCancelClick () {
        if (this.isTerminalWaiting) {
          this.showWaitingCloseConfirm = true
          return
        }
        this.close()
      },
      confirmCloseWhileWaiting () {
        this.showWaitingCloseConfirm = false
        if (this.isTerminalWaiting) this.close()
      },
      close () {
        if (this.isTerminalWaiting) {
          this.showInfoMessage('Терминал ещё выполняет оплату. Итог придёт уведомлением, карточка заказа обновится сама.')
        } else if (this.terminalState === 'applying') {
          this.showInfoMessage('Оплата записывается. Итог придёт уведомлением, карточка заказа обновится сама.')
        }
        this.$store.commit('newOrder/setPaymentMode', null)
        this.closeDialog()
      },
      closeDialog () {
        this.$store.commit('orders/setShowPaymentDialog', false)
      },
    },
  }
</script>

<style lang="less" scoped>
// оболочка (радиус 16, анимация, тень) и кнопки берутся из глобальных стилей PrimaryConfirmDialog
// через content-class primary-confirm-dialog; здесь только раскладка двух колонок
.payment-dialog {
  &-content {
    background-color: #fff;
    padding: 0 12px;
    border-radius: 16px;
  }
}
.left-side-menu {
  background-color: #f1f3f6;
  padding: 32px 24px;
  border-radius: 16px 0 0 16px;
}

.cancel-button {
  flex: 1 1 auto;
}
.manual-button {
  flex: 0 0 auto;
}
// с левого края роли и имени в UserInfo (аватар 59px + отступ 24px) при любой длине номера
.order-code {
  padding-left: 83px;
  font-size: 26px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: #111;
}
</style>
